import {LlmModelsResponse, LlmProvider} from '../services/ai/llm-models.service';
import {isGpt4FamilyOpenAiModel} from '../models/models.model';

export interface LlmModelOption {
  value: string;
  label: string;
  provider: LlmProvider;
  configured: boolean;
}

const PROVIDER_LABELS: Record<LlmProvider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
};

export function buildLlmModelOptions(
  response: LlmModelsResponse | null | undefined,
): LlmModelOption[] {
  const options: LlmModelOption[] = [];
  const seen = new Set<string>();

  const providers = response?.providers ?? [];
  for (const providerInfo of providers) {
    const provider = providerInfo.provider;
    const configured = providerInfo.configured;
    const models = provider === 'openai'
      ? buildOpenAiModelList(providerInfo.models ?? [])
      : providerInfo.models ?? [];
    for (const model of models) {
      const trimmed = (model ?? '').trim();
      if (!trimmed) continue;
      const value = toStoredModelValue(provider, trimmed);
      if (seen.has(value)) continue;
      seen.add(value);
      options.push({
        provider,
        configured,
        value,
        label: `${PROVIDER_LABELS[provider]} · ${trimmed}`,
      });
    }
  }

  return options.sort((a, b) => {
    const providerCmp = a.provider.localeCompare(b.provider);
    if (providerCmp !== 0) return providerCmp;
    return a.label.localeCompare(b.label);
  });
}

function buildOpenAiModelList(models: string[]): string[] {
  return models.filter((model) => !isGpt4FamilyOpenAiModel(model));
}

export function filterLlmModelOptions(options: LlmModelOption[], query: string | null | undefined): LlmModelOption[] {
  const q = (query ?? '').trim().toLowerCase();
  if (!q) return options;

  return options.filter((opt) => {
    const hay = `${opt.label} ${opt.value}`.toLowerCase();
    return hay.includes(q);
  });
}

export function modelValueToLabel(value: string | null | undefined, options: LlmModelOption[]): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  const match = options.find((o) => o.value === raw);
  if (match) return match.label;
  return raw;
}

function toStoredModelValue(provider: LlmProvider, model: string): string {
  if (provider === 'openai') return model; // keep backward-compatible
  return `${provider}:${model}`;
}
